import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Select from 'react-select';
import {
  Button,
  ButtonGroup,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModalTop } from '../../../actions';
import { showConfirmationModal } from '../../../actions/App';

import AdminEditButtons from '../EditButtons';
import AdminShareSelectedTemplate from '../ShareSelectedTemplate';
import HelpIcon from '../../HelpIcon';
import ProcessingSpinner from '../../ProcessingSpinner';
import ErrorBoundary from '../../ErrorBoundary';
import UpgradePlanButton from '../../UpgradePlanButton';
import UpgradeBoard from '../../UpgradeBoard';

import { getSetting } from '../../../helpers/Settings';
import { userHasFeature } from '../../../helpers/Features';

const AdminUserEditShareSettings = (props) => {
  const intl = useIntl();

  const {
    handleShareTemplatesAdd,
    handleShareTemplatesChange,
    handleShareTemplatesRemove,
    handleSelectedTemplateUuid,
    selectedTemplateUuid,
    openConfirmationModal,
    handleModal,
    templates,
    loadingAddTemplate,
    loadingRemoveTemplate,
  } = props;

  const shareTemplatesOptions = templates.map(p => ({ label: p.name, value: p.uuid }));
  const selectedTemplate = templates.length > 0 ?
    templates.find(p => p.uuid === selectedTemplateUuid) :
    null;

  useEffect(() => {
    if (selectedTemplateUuid === null && templates.length > 0) {
      handleSelectedTemplateUuid(templates[0].uuid);
    }
  }, [handleSelectedTemplateUuid, selectedTemplateUuid, templates]);

  const handleChangeTemplate = (option) => {
    handleSelectedTemplateUuid(option.value);
  };

  const handleShareTemplatesDeleteWrapper = () => {
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({ defaultMessage: 'Do you want to remove this template?' }),
      processingText: intl.formatMessage({ defaultMessage: 'Removing' }),
      onConfirm: () => {
        handleShareTemplatesRemove(selectedTemplateUuid);
        return Promise.resolve();
      },
    };
    openConfirmationModal(modalParams);
  };

  const handleShareTemplatesAddModal = () => {
    const handleConfirm = (name) => {
      handleShareTemplatesAdd(name);
    };
    handleModal(null, handleConfirm);
  };

  const handleShareTemplatesEditModal = (name, editKey) => {
    const handleConfirm = (newName, key) => {
      handleShareTemplatesChange(key, 'name', newName);
    };
    handleModal(name, handleConfirm, editKey);
  };

  if (!userHasFeature('share-templates')) {
    return (
      <ErrorBoundary>
        <div className="tab-content-wrapper p-0">
          <UpgradeBoard feature="share-templates" />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className="tab-content-wrapper share-settings-tab">
        <h4>
          {intl.formatMessage({ defaultMessage: 'Share Settings' })}
        </h4>
        {getSetting('share_templates_user_create') ? (
          <p>
            {intl.formatMessage({
              defaultMessage: 'Set your default share link settings and manage personal share templates.',
            })}
            <HelpIcon url="https://help.stockpress.co/en/articles/5937349-share-files-and-collections" />
          </p>
        ) : (
          <p>
            {intl.formatMessage({ defaultMessage: 'Set your personal default share link settings.' })}
            <HelpIcon url="https://help.stockpress.co/en/articles/5937349-share-files-and-collections" />
          </p>
        )}
        {getSetting('share_templates_user_create') && (
          <>
            <hr />
            <h5>
              {intl.formatMessage({ defaultMessage: 'Templates' })}
              <UpgradePlanButton
                feature="share-templates"
              />
            </h5>
            <div className="share-settings-templates-controls">
              <Select
                className="share-settings-select-templates"
                classNamePrefix="share-settings-select-templates"
                options={shareTemplatesOptions}
                value={
                  selectedTemplate ?
                    { value: selectedTemplate.uuid, label: selectedTemplate.name }
                    : null
                }
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                onChange={handleChangeTemplate}
                isDisabled={!userHasFeature('share-templates')}
              />
              <ButtonGroup>
                <Button
                  id="ShareSettingsEditTemplate"
                  color="primary"
                  onClick={
                    () => handleShareTemplatesEditModal(selectedTemplate.name, selectedTemplateUuid)
                  }
                  disabled={templates.length === 0 || !userHasFeature('share-templates')}
                >
                  <i className="far fa-edit" />
                </Button>
                <Button
                  id="ShareSettingsRemoveTemplate"
                  color="primary"
                  onClick={() => handleShareTemplatesDeleteWrapper(selectedTemplateUuid)}
                  disabled={templates.length === 0 || loadingRemoveTemplate || !userHasFeature('share-templates')}
                >
                  {loadingRemoveTemplate ? (
                    <ProcessingSpinner show={loadingRemoveTemplate} />
                  ) : (
                    <i className="far fa-trash" />
                  )}
                </Button>
              </ButtonGroup>
              <Button
                id="ShareSettingsAddTemplate"
                color="primary"
                onClick={() => handleShareTemplatesAddModal()}
                disabled={loadingAddTemplate || !userHasFeature('share-templates')}
              >
                {loadingAddTemplate ? (
                  <ProcessingSpinner show={loadingAddTemplate} />
                ) : (
                  <i className="far fa-plus" />
                )}
              </Button>
            </div>
          </>
        )}

        {selectedTemplate && (
          <AdminShareSelectedTemplate
            selectedTemplateUuid={selectedTemplateUuid}
            templates={templates}
            handleShareTemplatesChange={handleShareTemplatesChange}
            handleSelectedTemplateUuid={handleSelectedTemplateUuid}
          />
        )}
        {(!selectedTemplate && userHasFeature('share-templates')) && (
          <p>{intl.formatMessage({ defaultMessage: 'No templates. You can create a new one (+).' })}</p>
        )}
      </div>
      <AdminEditButtons
        {...props}
        hideDelete={true}
      />
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  client: state.Auth.profile?.client,
});

const mapDispatchToProps = dispatch => ({
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  handleModal: (name, handleConfirm, editKey = null) => {
    dispatch(showModalTop('ShareTemplateModal', {
      name,
      handleConfirm,
      editKey,
    }));
  },
});

AdminUserEditShareSettings.propTypes = {
  client: PropTypes.object.isRequired,
  handleShareTemplatesAdd: PropTypes.func.isRequired,
  handleShareTemplatesChange: PropTypes.func.isRequired,
  handleShareTemplatesRemove: PropTypes.func.isRequired,
  handleSelectedTemplateUuid: PropTypes.func.isRequired,
  selectedTemplateUuid: PropTypes.string,
  openConfirmationModal: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired,
  templates: PropTypes.array.isRequired,
  loadingAddTemplate: PropTypes.bool.isRequired,
  loadingRemoveTemplate: PropTypes.bool.isRequired,
};

AdminUserEditShareSettings.defaultProps = {
  selectedTemplateUuid: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminUserEditShareSettings);
